import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BackHandler,
  Platform,
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import AppBackground from './AppBackground';
import { AppErrorBoundary } from './AppErrorBoundary';
import ShareWrapper from './ShareWrapper';
import GlobalErrorBoundary from './GlobalErrorBoundary';

import { ROUTES } from '../shared/constants/routes';
import { FOOTER_HEIGHT, FULL_WIDTH } from '../shared/constants/dimension';

import HomeScreenHeader from './HomeScreenHeader';
import CollectionTabs from './CollectionTabs';
import Footer from '../Footer';
import BottomModal from './BottomModal';
import StickyAiAssistant from './StickyAiAssistant';
import AudioPlayer from './AudioPlayer';
import AppLoader from './AppLoader';
import { CustomIcon } from './CustomIcon';

import { HomeScreen } from '../routes/home';
import { Login } from '../routes/login';
import SettingsScreen from '../features/settings/screens/SettingsScreen';
import FAQScreen from '../features/settings/screens/FaqScreen';
import TermsScreen from '../features/settings/screens/TermsScreen';
import PrivacyScreen from '../features/settings/screens/PrivacyScreen';
import ContactUsScreen from '../features/settings/screens/ContactUsScreen';
import { ImmersiveNewsArticle } from '../features/immersive-news-article';
import EditCollectionScreen from '../features/Collections/EditCollectionScreen';
import RecommendedCollectionsScreen from '../features/Collections/RecommendedCollectionsScreen';

import { useActiveTabStore } from '../store/activeTabStore';
import { useActiveArticleStore } from '../store/activeArticleStore';
import { useNavStore } from '../store/navStore';

import { useThemeSettings } from '../ThemeProvider';
import getGlobalStyles from '../globalStyles';
import { usePlatform } from '../providers/PlatformProvider';
import { useModalStore, ModalType } from '../store/modalStore';

const EMPTY_TITLE_TO_SHOW_BACK_BUTTON = '  ';

export default function ScreenComponent() {

const { width } = useWindowDimensions();
  const [title, setTitle] = useState('');

  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();

  const { theme } = useThemeSettings();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const { showWebVersion } = usePlatform();
  const { modalShown, clearModal } = useModalStore();

  const setActiveTab = useActiveTabStore((s) => s.setActiveTab);
  const activetab = useActiveTabStore((s) => s.activetab);
  const setActiveArticleView = useActiveArticleStore(
    (s) => s.setActiveArticleView
  );

  const immersiveBottomSpacing =
    Platform.OS!=='web' && route?.name === ROUTES.IMMERSRSIVE_NEWS_ARTICLE ? 16 : 0;
  const hidesFooter =
    route?.name === ROUTES.WELCOME ||
    route?.name === ROUTES.WELCOME_ONBOARDING ||
    route?.name === ROUTES.WEB ||
    route?.name === ROUTES.RECOMMENDED_COLLECTIONS;

  const goBack = useCallback(() => {
    const previous = useNavStore.getState().popRoute();
    if (previous) {
      navigation.navigate(previous.name, previous.params);
    } else {
      navigation.navigate(ROUTES.HOME, { selectedTab: activetab });
    }
  }, [navigation, activetab]);

  /**
   * SIDE EFFECTS BASED ON ROUTE
   * (titles, active article, active tab)
   */
  useEffect(() => {
    switch (route?.name) {
      case ROUTES.HOME:
        setTitle('');
        if (route?.params?.selectedTab) {
          setActiveTab(route.params.selectedTab.toLowerCase());
        }
        break;

      case ROUTES.IMMERSRSIVE_NEWS_ARTICLE:
        setActiveArticleView('GIST');
        setTitle(EMPTY_TITLE_TO_SHOW_BACK_BUTTON);
        break;

      case ROUTES.LOGIN:
        setTitle(t('screens.login.signIn'));
        break;

      case ROUTES.SETTINGS:
        setTitle('Settings');
        break;

      case ROUTES.FAQ:
        setTitle('FAQs');
        break;

      case ROUTES.CONTACT_US:
        setTitle('Contact Us');
        break;

      case ROUTES.TERMS:
        setTitle('Terms and Conditions');
        break;

      case ROUTES.PRIVACY:
        setTitle('Privacy Policy');
        break;

      case ROUTES.RADIO_PLAYER:
        setTitle(EMPTY_TITLE_TO_SHOW_BACK_BUTTON);
        break;

      case ROUTES.TBD:
        setTitle(EMPTY_TITLE_TO_SHOW_BACK_BUTTON);
        break;

      case ROUTES.CREATE_COLLECTION:
        setTitle(t('screens.collections.createTitle'));
        break;

      case ROUTES.EDIT_COLLECTION:
        setTitle(t('screens.collections.editTitle'));
        break;

      case ROUTES.RECOMMENDED_COLLECTIONS:
        setTitle(t('screens.collections.recommendedPickerTitle'));
        break;
    }
  }, [route?.name, route?.params, t, setActiveTab, setActiveArticleView]);

  useEffect(() => {
    if (Platform.OS !== 'android') return;

    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (modalShown !== ModalType.NONE) {
        clearModal();
        return true;
      }
      if (
        route?.name === ROUTES.CREATE_COLLECTION ||
        route?.name === ROUTES.EDIT_COLLECTION ||
        route?.name === ROUTES.RECOMMENDED_COLLECTIONS
      ) {
        goBack();
        return true;
      }
      return false;
    });

    return () => sub.remove();
  }, [modalShown, clearModal]);

  /**
   * PURE RENDER FUNCTION
   */
  const renderScreen = () => {
    switch (route?.name) {
      case ROUTES.HOME:
        return (
          <>
            {!showWebVersion && <HomeScreenHeader />}
            {activetab.toLowerCase() !== ROUTES.WATCH && <CollectionTabs />}
            <HomeScreen />
          </>
        );
      case ROUTES.IMMERSRSIVE_NEWS_ARTICLE:
        return <ImmersiveNewsArticle />;

      case ROUTES.LOGIN:
        return <Login />;

      case ROUTES.SETTINGS:
        return <SettingsScreen />;

      case ROUTES.FAQ:
        return <FAQScreen />;

      case ROUTES.CONTACT_US:
        return <ContactUsScreen />;

      case ROUTES.TERMS:
        return <TermsScreen />;

      case ROUTES.PRIVACY:
        return <PrivacyScreen />;

      case ROUTES.RADIO_PLAYER:
        return <AudioPlayer />;

      case ROUTES.TBD:
        return (
          <AppLoader
            icon="hour-glass"
            isLoading={false}
            title={t('screens.comingSoon.title')}
            description={t('screens.comingSoon.subtitle')}
          />
        );

      case ROUTES.CREATE_COLLECTION:
      case ROUTES.EDIT_COLLECTION:
        return (
          <EditCollectionScreen onClose={goBack} />
        );

      case ROUTES.RECOMMENDED_COLLECTIONS:
        return <RecommendedCollectionsScreen />;
    }
  };

  return (
    <ShareWrapper>
      {(viewRef: any) => (
        <AppBackground ref={viewRef}>
          <View
            style={{
              flex: 1,
              maxWidth: FULL_WIDTH,
              width,
              paddingHorizontal: (showWebVersion && activetab.toLowerCase() !== ROUTES.WATCH) ? 96 : 0,
              paddingBottom: showWebVersion || hidesFooter ? 0 : FOOTER_HEIGHT + immersiveBottomSpacing,
            }}
          >
            <AppErrorBoundary>
              <GlobalErrorBoundary>
                {title && (
                  <View
                    style={[
                      styles.header,
                      {
                        paddingTop: insets.top + 10,
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                      },
                      gStyles.webCard,
                    ]}
                  >
                    <TouchableOpacity
                      style={[gStyles.buttonStyle, gStyles.buttonCircle]}
                      onPress={goBack}
                    >
                      <CustomIcon
                        icon="chevron-left"
                        color={theme.colors.primary}
                      />
                    </TouchableOpacity>

                    <Text
                      style={[
                        styles.headerTitle,
                        { color: theme.colors.primary },
                      ]}
                    >
                      {title}
                    </Text>
                  </View>
                )}

                <View style={{ flex: 1 }}>{renderScreen()}</View>
              </GlobalErrorBoundary>
            </AppErrorBoundary>
          </View>

          <Footer />
          <BottomModal />
          {showWebVersion && <StickyAiAssistant />}
        </AppBackground>
      )}
    </ShareWrapper>
  );
}

const styles = StyleSheet.create({
  header: {
    paddingTop: Platform.OS === 'ios' ? 60 : 30,
    paddingBottom: 10,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
  },
});
